import { AutomationTemplateTenantsModeEnum } from '@reach/shared-types/automation-template';
import type { AutomationTemplate } from '../templates.types';
import { StatusFilter, type TemplateListStatus } from './types';

/**
 * Draft is keyed off an empty tenant selection — never off a resolved-count of 0, so an
 * all-tenants template on a platform with zero businesses is not misclassified as Draft.
 */
export function deriveTemplateStatus(template: AutomationTemplate): TemplateListStatus {
  const { tenants } = template;
  const templateIsDraft =
    tenants.mode === AutomationTemplateTenantsModeEnum.SELECTED && tenants.tenantIds.length === 0;
  if (templateIsDraft) {
    return StatusFilter.Draft;
  }
  return (template.activeBusinessIds?.length ?? 0) > 0 ? StatusFilter.Active : StatusFilter.Available;
}

/**
 * Business IDs a template is synced/available to: every current tenant except excluded ones for an
 * all-tenants template, otherwise the selected tenants intersected with the current roster (drops
 * tenants that no longer exist).
 */
export function resolveSyncedBusinessIds(
  template: AutomationTemplate,
  rosterBusinessIds: string[],
): string[] {
  const { tenants } = template;
  if (tenants.mode === AutomationTemplateTenantsModeEnum.ALL) {
    const excludedIdSet = new Set(tenants.excludedTenantIds);
    return rosterBusinessIds.filter(id => !excludedIdSet.has(id));
  }
  const rosterSet = new Set(rosterBusinessIds);
  return tenants.tenantIds.filter(id => rosterSet.has(id));
}

/** Business IDs with the template enabled, intersected with the current roster. */
export function resolveActiveBusinessIds(
  template: AutomationTemplate,
  rosterBusinessIds: string[],
): string[] {
  const rosterSet = new Set(rosterBusinessIds);
  return (template.activeBusinessIds ?? []).filter(id => rosterSet.has(id));
}
